import React, { useState, useEffect } from 'react';
import qs from 'qs';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import {
  Typography,
  Button,
  InputBase,
  InputLabel,
  Link,
} from '@mui/material';
import api from '~/_base/constants/api';
import { SSOProvider } from '~/models';
import { useToast } from '~/_base/context/toastContext';
import logger from '~/_base/logger';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

export default function SignIn() {
  const classes = useStyles();
  const { errorToast } = useToast();
  const queryString = (document.location.search || '').slice(1);
  const params = qs.parse(queryString);
  const [email, setEmail] = useState(params['email'] || '');
  const [password, setPassword] = useState('');
  const [ssoProviders, setSsoProviders] = useState<SSOProvider[]>([]);
  const { i18n, t } = useTranslation();

  useEffect(() => {
    async function getSSOProviders() {
      try {
        const json = await api.ssoProviders.request();
        setSsoProviders(json.providers);
      } catch (err) {
        errorToast(
          `An error occured while fetching SSO providers: ${(err as Error).message}`
        );
      }
    }

    getSSOProviders();
  }, [errorToast]);

  useEffect(() => {
    const lang = document.documentElement.lang;
    i18n.changeLanguage(lang);
    moment.locale(lang);
  }, [i18n]);

  async function handleSigninClick() {
    logger.info('Sign in button clicked');
    try {
      const json = await api.signin.request(
        JSON.stringify({
          session: {
            email,
            password,
          },
        })
      );
      if (json.location) {
        sessionStorage.setItem('auth_method', 'Native');
        document.location.href = json.location;
      }
    } catch (err) {
      errorToast((err as Error).message);
    }
  }

  function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      handleSigninClick();
    }
  }

  return (
    <React.Fragment>
      {ssoProviders.length > 0 ? (
        <div className={classes.container}>
          <div className={classes.ssoProviders}>
            {ssoProviders.map((item, index) => (
              <a
                key={index}
                href={item.path}
                className={classes.ssoLink}
                data-method={item?.method || 'get'}
                onClick={() => {
                  try {
                    sessionStorage.setItem('auth_method', 'SSO');
                  } catch (e) {
                    console.error(e);
                  }
                }}
              >
                <Button variant="contained" color="secondary" className={classes.ssoButton}>
                  <img
                    src={item.logo}
                    alt={`sso button for ${item.name}`}
                    className={classes.ssoImage}
                  />
                  <Typography variant="body1" className={classes.ssoText}>
                    {t('Sign in with {{name}}', { name: item.name })}
                  </Typography>
                </Button>
              </a>
            ))}
          </div>

          <div className={classes.orContainer}>
            <div className={classes.orLine} />
            <div className={classes.orText}>{t('or')}</div>
          </div>
        </div>
      ) : null}
      <div className={clsx([classes.container, classes.inputContainer])}>
        <InputLabel htmlFor="session_email">{t('Email')}</InputLabel>
        <InputBase
          onKeyPress={handleKeyPress}
          id="session_email"
          name="session[email]"
          value={email}
          fullWidth
          type="email"
          onChange={e => setEmail(e.currentTarget.value)}
          placeholder={t('Email Address')}
          autoComplete="off"
        />
      </div>
      <div className={clsx([classes.container, classes.inputContainer])}>
        <InputLabel htmlFor="session_password">{t('Password')}</InputLabel>
        <InputBase
          onKeyPress={handleKeyPress}
          id="session_password"
          value={password}
          onChange={e => setPassword(e.currentTarget.value)}
          name="session[password]"
          type="password"
          fullWidth
          placeholder={t('Password')}
          autoComplete="off"
        />
      </div>
      <div className={clsx([classes.container, classes.inputContainer])}>
        <Button
          className={classes.button}
          variant="contained"
          color="primary"
          id="apeSignInBtn"
          onClick={() => handleSigninClick()}
        >
          {t('Sign in')}
        </Button>
      </div>
      <div className={classes.info}>
        <Link
          onClick={() => logger.info('Forgotten password link clicked')}
          href="/password_resets/new"
          sx={{ fontStyle: 'italic' }}>
          {t('Forgotten password?')}
        </Link>
        <Typography variant="body1" className={classes.infoText}>
          {t(
            'New user? Ask your systems administrator or project manager to create your account.'
          )}
        </Typography>
      </div>
    </React.Fragment>
  );
}

const useStyles = makeStyles(theme => ({
  info: {
    maxWidth: 328,
    margin: '15px auto 0',
    textAlign: 'center',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    maxWidth: 328,
    margin: '0 auto',
  },
  button: {
    minWidth: '100%',
  },
  ssoProviders: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  ssoLink: {
    width: '100%',
    margin: theme.spacing(0, 0, 2, 0),
    padding: 0,
    textDecoration: 'none',
  },
  ssoButton: {
    margin: 0,
    padding: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  ssoImage: {
    height: 40,
    width: 40,
  },
  ssoText: {
    width: '100%',
  },
  orContainer: {
    position: 'relative',
    zIndex: 0,
    display: 'flex',
    justifyContent: 'center',
    margin: '15px 0',
  },
  orLine: {
    position: 'absolute',
    width: '100%',
    top: 8,
    left: 0,
    borderBottom: 'solid 1px #e6e6e6',
    zIndex: -1,
  },
  orText: {
    color: '#6f6f6f',
    textAlign: 'center',
    fontStyle: 'italic',
    width: 36,
    background: '#fff',
    zIndex: 1,
    fontSize: 12,
  },
  container: {
    maxWidth: 328,
    margin: '0 auto',
  },
  inputContainer: {
    marginTop: '1.5rem',
  },
  infoText: {
    marginTop: theme.spacing(5),
    paddingTop: theme.spacing(4),
    borderTop: '1px solid #e6eaec',
  },
}));
